import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  Grid,
  Divider
} from '@mui/material';
import { fetchWithAuth } from '../../utils/auth';
import API_BASE_URL from '../../config/api';
import {
  ExpandMore as ExpandMoreIcon,
  Lightbulb as LightbulbIcon,
  Science as ScienceIcon,
  Timeline as TimelineIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  QuestionMark as QuestionMarkIcon,
  Article as ArticleIcon,
  TrendingUp as TrendingUpIcon,
  AutoAwesome as AutoAwesomeIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import '../../assets/css/futuristic-modern.css';

function ResearchInsights({ searchCriteria, onArticleClick }) {
  const [insights, setInsights] = useState({});
  const [loading, setLoading] = useState({});
  const [errors, setErrors] = useState({});
  const [expandedQuery, setExpandedQuery] = useState(null);
  const [downloadingPptx, setDownloadingPptx] = useState({});

  const fetchInsights = async (queryIndex) => {
    setLoading(prev => ({ ...prev, [queryIndex]: true }));
    setErrors(prev => ({ ...prev, [queryIndex]: null }));

    try {
      // 🛡️ SAFETY: Get unique query ID from searchCriteria
      const uniqueQueryId = searchCriteria[queryIndex]?.unique_query_id;
      
      // Build URL with both IDs (backend prefers unique_query_id)
      const url = uniqueQueryId 
        ? `/api/query-insights/${queryIndex}?unique_query_id=${uniqueQueryId}`
        : `/api/query-insights/${queryIndex}`;
      
      const response = await fetchWithAuth(url);
      const data = await response.json();

      if (data.success) {
        setInsights(prev => ({ ...prev, [queryIndex]: data }));
      } else {
        setErrors(prev => ({ ...prev, [queryIndex]: data.error }));
      }
    } catch (error) {
      console.error('Error fetching insights:', error);
      setErrors(prev => ({ ...prev, [queryIndex]: error.message }));
    } finally {
      setLoading(prev => ({ ...prev, [queryIndex]: false }));
    }
  };

  const handleAccordionChange = (queryIndex) => (event, isExpanded) => {
    setExpandedQuery(isExpanded ? queryIndex : null);
    
    // Fetch insights when expanding if not already loaded
    if (isExpanded && !insights[queryIndex] && !loading[queryIndex]) {
      fetchInsights(queryIndex);
    }
  };

  const downloadPresentation = async (queryIndex) => {
    setDownloadingPptx(prev => ({ ...prev, [queryIndex]: true }));
    
    try {
      // 🛡️ SAFETY: Get unique query ID from searchCriteria
      const uniqueQueryId = searchCriteria[queryIndex]?.unique_query_id;
      
      // Build URL with both IDs (backend prefers unique_query_id)
      const apiUrl = uniqueQueryId
        ? `/api/generate-presentation/${queryIndex}?unique_query_id=${uniqueQueryId}`
        : `/api/generate-presentation/${queryIndex}`;
      
      // Use fetchWithAuth which handles token refresh
      const response = await fetchWithAuth(apiUrl, {
        method: 'GET'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate presentation');
      }

      // Get filename from Content-Disposition header or create default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'Research_Synthesis.pptx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Download file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('✅ PowerPoint downloaded successfully');
    } catch (error) {
      console.error('Error downloading presentation:', error);
      setErrors(prev => ({ ...prev, [queryIndex]: error.message }));
    } finally {
      setDownloadingPptx(prev => ({ ...prev, [queryIndex]: false }));
    }
  };

  const getConfidenceColor = (confidence) => {
    switch(confidence?.toLowerCase()) {
      case 'high': return 'var(--success)';
      case 'medium': return 'var(--warning)';
      case 'low': return 'var(--error)';
      default: return 'var(--text-muted)';
    }
  };

  const getConfidenceIcon = (confidence) => {
    switch(confidence?.toLowerCase()) {
      case 'high': return <CheckCircleIcon sx={{ fontSize: 16 }} />;
      case 'medium': return <QuestionMarkIcon sx={{ fontSize: 16 }} />;
      case 'low': return <WarningIcon sx={{ fontSize: 16 }} />;
      default: return null;
    }
  };

  return (
    <Box sx={{ width: '100%', pt: 1 }}>
      {searchCriteria && searchCriteria.length > 0 ? (
        <Box>
          {searchCriteria.map((criteria, index) => (
            <Accordion
              key={index}
              expanded={expandedQuery === index}
              onChange={handleAccordionChange(index)}
              sx={{
                marginBottom: '1rem !important',
                backgroundColor: 'var(--surface-2)',
                border: '1px solid var(--glass-border)',
                borderRadius: 'var(--radius-lg)',
                boxShadow: 'var(--shadow-md)',
                '&:before': { display: 'none' },
                '&.MuiAccordion-root': {
                  marginBottom: '1rem !important'
                },
                '& + .MuiAccordion-root': {
                  marginTop: '0px !important'
                },
                '&:hover': {
                  borderColor: 'var(--neon-cyan)',
                  boxShadow: '0 4px 20px rgba(108, 48, 130, 0.3)'
                }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: 'var(--text-primary)' }} />}
                sx={{
                  backgroundColor: 'var(--surface-2)',
                  borderRadius: 'var(--radius-lg)',
                  minHeight: '64px',
                  '&:hover': {
                    backgroundColor: 'rgba(108, 48, 130, 0.05)'
                  },
                  '& .MuiAccordionSummary-content': {
                    margin: '12px 0'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                  <AutoAwesomeIcon sx={{ color: 'var(--neon-cyan)', fontSize: '1.5rem' }} />
                  <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: 600 }}>
                    {criteria.criteria[0]}
                  </Typography>
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ p: 3, backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                {loading[index] ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
                    <Box className="loading-spinner-modern" sx={{ mb: 3 }}></Box>
                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '1rem', fontWeight: 500 }}>
                      Analyzing articles and generating insights...
                    </Typography>
                    <Typography sx={{ color: 'var(--text-muted)', fontSize: '0.875rem', mt: 1 }}>
                      This may take 10-30 seconds
                    </Typography>
                  </Box>
                ) : errors[index] ? (
                  <Box
                    sx={{
                      p: 3,
                      backgroundColor: 'rgba(244, 67, 54, 0.05)',
                      border: '1px solid var(--error)',
                      borderRadius: 'var(--radius-md)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2
                    }}
                  >
                    <WarningIcon sx={{ color: 'var(--error)', fontSize: '2rem' }} />
                    <Box>
                      <Typography sx={{ color: 'var(--error)', fontWeight: 600, mb: 0.5 }}>
                        Error Generating Insights
                      </Typography>
                      <Typography sx={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                        {errors[index]}
                      </Typography>
                    </Box>
                  </Box>
                ) : insights[index]?.insights ? (
                  <Box>
                    {/* Header with stats and download button */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 3,
                      pb: 2,
                      borderBottom: '1px solid var(--glass-border)'
                    }}>
                      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        <Chip
                          icon={<ArticleIcon sx={{ fontSize: '1rem' }} />}
                          label={`${insights[index].article_count} articles analyzed`}
                          sx={{ 
                            backgroundColor: 'rgba(108, 48, 130, 0.15)',
                            color: 'var(--text-primary)',
                            border: '1px solid rgba(108, 48, 130, 0.3)',
                            fontWeight: 600,
                            fontSize: '0.875rem'
                          }}
                        />
                        {insights[index].total_articles > insights[index].article_count && (
                          <Chip
                            label={`${insights[index].total_articles - insights[index].article_count} more available`}
                            size="small"
                            sx={{ 
                              backgroundColor: 'var(--surface-hover)', 
                              color: 'var(--text-muted)',
                              border: '1px solid var(--border)',
                              fontSize: '0.75rem'
                            }}
                          />
                        )}
                      </Box>
                      
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadPresentation(index);
                        }}
                        disabled={downloadingPptx[index]}
                        startIcon={downloadingPptx[index] ? <CircularProgress size={16} /> : <DownloadIcon />}
                        className="btn-modern-primary"
                        size="small"
                        sx={{
                          background: 'var(--accent-gradient)',
                          color: 'white',
                          fontWeight: 600,
                          px: 2,
                          py: 0.75,
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(108, 48, 130, 0.4)'
                          },
                          '&:disabled': {
                            background: 'var(--surface-hover)',
                            color: 'var(--text-muted)'
                          }
                        }}
                      >
                        {downloadingPptx[index] ? 'Generating...' : 'Download PowerPoint'}
                      </Button>
                    </Box>

                    {/* Summary */}
                    {insights[index].insights.summary && (
                      <Box
                        className="modern-card"
                        sx={{
                          p: 3,
                          mb: 3,
                          background: 'linear-gradient(135deg, rgba(108, 48, 130, 0.1) 0%, rgba(70, 130, 48, 0.05) 100%)',
                          border: '2px solid var(--neon-cyan)',
                          borderRadius: 'var(--radius-md)',
                          position: 'relative',
                          overflow: 'hidden',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '4px',
                            background: 'var(--accent-gradient)',
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          <LightbulbIcon sx={{ color: 'var(--neon-cyan)', fontSize: '1.75rem', mt: 0.5 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" sx={{ color: 'var(--neon-cyan)', mb: 1, fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                              Executive Summary
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'var(--text-primary)', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {insights[index].insights.summary}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}

                    <Grid container spacing={3}>
                      {/* Key Findings */}
                      {insights[index].insights.key_findings && insights[index].insights.key_findings.length > 0 && (
                        <Grid item xs={12}>
                          <Box className="modern-card" sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 2, borderBottom: '1px solid var(--glass-border)' }}>
                              <LightbulbIcon sx={{ color: 'var(--neon-cyan)', mr: 1.5, fontSize: '1.5rem' }} />
                              <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                                Key Findings
                              </Typography>
                              <Chip
                                label={insights[index].insights.key_findings.length}
                                size="small"
                                sx={{
                                  ml: 1.5,
                                  backgroundColor: 'var(--neon-cyan)',
                                  color: 'white',
                                  fontWeight: 700,
                                  minWidth: '28px'
                                }}
                              />
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              {insights[index].insights.key_findings.map((finding, idx) => (
                                <Box
                                  key={idx}
                                  className="modern-card"
                                  sx={{
                                    p: 2.5,
                                    backgroundColor: 'var(--surface-2)',
                                    border: '1px solid var(--glass-border)',
                                    borderRadius: 'var(--radius-md)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      borderColor: 'var(--neon-cyan)',
                                      boxShadow: '0 4px 12px rgba(108, 48, 130, 0.2)',
                                      transform: 'translateX(4px)'
                                    }
                                  }}
                                >
                                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                    <Box sx={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      justifyContent: 'center',
                                      minWidth: 36,
                                      height: 36,
                                      borderRadius: '50%',
                                      background: 'var(--accent-gradient)',
                                      color: 'white',
                                      fontWeight: 700,
                                      fontSize: '1.1rem',
                                      flexShrink: 0
                                    }}>
                                      {idx + 1}
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                      <Typography sx={{ 
                                        color: 'var(--text-primary)', 
                                        fontWeight: 500,
                                        fontSize: '1rem',
                                        lineHeight: 1.6,
                                        mb: 1.5,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word'
                                      }}>
                                        {finding.finding}
                                      </Typography>
                                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Chip
                                          icon={getConfidenceIcon(finding.confidence)}
                                          label={`${finding.confidence || 'Unknown'} Confidence`}
                                          size="small"
                                          sx={{
                                            backgroundColor: getConfidenceColor(finding.confidence) + '22',
                                            color: getConfidenceColor(finding.confidence),
                                            border: `1.5px solid ${getConfidenceColor(finding.confidence)}`,
                                            fontWeight: 600,
                                            fontSize: '0.75rem'
                                          }}
                                        />
                                        <Chip
                                          icon={<ArticleIcon sx={{ fontSize: '0.9rem' }} />}
                                          label={`${finding.supporting_article_numbers?.length || 0} supporting articles`}
                                          size="small"
                                          sx={{
                                            backgroundColor: 'rgba(108, 48, 130, 0.1)',
                                            color: 'var(--neon-cyan)',
                                            border: '1px solid rgba(108, 48, 130, 0.3)',
                                            fontWeight: 600,
                                            fontSize: '0.75rem'
                                          }}
                                        />
                                      </Box>
                                    </Box>
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {/* Common Methodologies */}
                      {insights[index].insights.common_methodologies && insights[index].insights.common_methodologies.length > 0 && (
                        <Grid item xs={12} md={6}>
                          <Box className="modern-card" sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 2, borderBottom: '1px solid var(--glass-border)' }}>
                              <ScienceIcon sx={{ color: 'var(--success)', mr: 1.5, fontSize: '1.5rem' }} />
                              <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                                Methodologies
                              </Typography>
                              <Chip
                                label={insights[index].insights.common_methodologies.length}
                                size="small"
                                sx={{
                                  ml: 1.5,
                                  backgroundColor: 'var(--success)',
                                  color: 'white',
                                  fontWeight: 700,
                                  minWidth: '28px'
                                }}
                              />
                            </Box>
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              {insights[index].insights.common_methodologies.map((method, idx) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    p: 2,
                                    backgroundColor: 'var(--surface-2)',
                                    border: '1px solid var(--glass-border)',
                                    borderRadius: 'var(--radius-sm)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      borderColor: 'var(--success)',
                                      backgroundColor: 'rgba(70, 130, 48, 0.05)'
                                    }
                                  }}
                                >
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography sx={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.95rem' }}>
                                      {method.methodology}
                                    </Typography>
                                    <Chip
                                      label={`${method.article_count} articles`}
                                      size="small"
                                      sx={{
                                        backgroundColor: 'var(--success)',
                                        color: 'white',
                                        fontSize: '0.7rem',
                                        fontWeight: 700,
                                        height: '22px'
                                      }}
                                    />
                                  </Box>
                                  <Typography sx={{ 
                                    color: 'var(--text-muted)', 
                                    fontSize: '0.85rem',
                                    lineHeight: 1.5,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                  }}>
                                    {method.description}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {/* Notable Results */}
                      {insights[index].insights.notable_results && insights[index].insights.notable_results.length > 0 && (
                        <Grid item xs={12} md={6}>
                          <Box className="modern-card" sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 2, borderBottom: '1px solid var(--glass-border)' }}>
                              <TrendingUpIcon sx={{ color: 'var(--warning)', mr: 1.5, fontSize: '1.5rem' }} />
                              <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                                Notable Results
                              </Typography>
                              <Chip
                                label={insights[index].insights.notable_results.length}
                                size="small"
                                sx={{
                                  ml: 1.5,
                                  backgroundColor: 'var(--warning)',
                                  color: 'white',
                                  fontWeight: 700,
                                  minWidth: '28px'
                                }}
                              />
                            </Box>
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              {insights[index].insights.notable_results.map((result, idx) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    p: 2,
                                    backgroundColor: 'var(--surface-2)',
                                    border: '1px solid var(--glass-border)',
                                    borderRadius: 'var(--radius-sm)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      borderColor: 'var(--warning)',
                                      backgroundColor: 'rgba(255, 193, 7, 0.05)'
                                    }
                                  }}
                                >
                                  <Typography sx={{ 
                                    color: 'var(--text-primary)', 
                                    fontWeight: 600, 
                                    mb: 1,
                                    fontSize: '0.95rem',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                  }}>
                                    {result.result}
                                  </Typography>
                                  <Typography sx={{ 
                                    color: 'var(--text-muted)', 
                                    fontStyle: 'italic',
                                    fontSize: '0.85rem',
                                    mb: 1,
                                    lineHeight: 1.5,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                  }}>
                                    {result.significance}
                                  </Typography>
                                  <Chip
                                    icon={<ArticleIcon sx={{ fontSize: '0.8rem' }} />}
                                    label={`${result.article_numbers?.length || 0} articles`}
                                    size="small"
                                    sx={{
                                      backgroundColor: 'rgba(255, 193, 7, 0.15)',
                                      color: 'var(--warning)',
                                      border: '1px solid var(--warning)',
                                      fontSize: '0.7rem',
                                      fontWeight: 600,
                                      height: '22px'
                                    }}
                                  />
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {/* Research Gaps */}
                      {insights[index].insights.research_gaps && insights[index].insights.research_gaps.length > 0 && (
                        <Grid item xs={12} md={6}>
                          <Box className="modern-card" sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 2, borderBottom: '1px solid var(--glass-border)' }}>
                              <WarningIcon sx={{ color: 'var(--error)', mr: 1.5, fontSize: '1.5rem' }} />
                              <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                                Research Gaps
                              </Typography>
                              <Chip
                                label={insights[index].insights.research_gaps.length}
                                size="small"
                                sx={{
                                  ml: 1.5,
                                  backgroundColor: 'var(--error)',
                                  color: 'white',
                                  fontWeight: 700,
                                  minWidth: '28px'
                                }}
                              />
                            </Box>
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              {insights[index].insights.research_gaps.map((gap, idx) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    p: 2,
                                    backgroundColor: 'var(--surface-2)',
                                    border: '1px solid var(--glass-border)',
                                    borderLeft: '4px solid var(--error)',
                                    borderRadius: 'var(--radius-sm)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      borderColor: 'var(--error)',
                                      backgroundColor: 'rgba(244, 67, 54, 0.05)',
                                      transform: 'translateX(4px)'
                                    }
                                  }}
                                >
                                  <Typography sx={{ 
                                    color: 'var(--text-primary)',
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    mb: 0.5,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                  }}>
                                    {typeof gap === 'string' ? gap : gap.gap}
                                  </Typography>
                                  {typeof gap === 'object' && gap.importance && (
                                    <Typography sx={{ 
                                      color: 'var(--text-muted)',
                                      fontSize: '0.85rem',
                                      lineHeight: 1.5,
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word'
                                    }}>
                                      {gap.importance}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {/* Clinical Implications */}
                      {insights[index].insights.clinical_implications && insights[index].insights.clinical_implications.length > 0 && (
                        <Grid item xs={12} md={6}>
                          <Box className="modern-card" sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 2, borderBottom: '1px solid var(--glass-border)' }}>
                              <TimelineIcon sx={{ color: 'var(--success)', mr: 1.5, fontSize: '1.5rem' }} />
                              <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                                Clinical Implications
                              </Typography>
                              <Chip
                                label={insights[index].insights.clinical_implications.length}
                                size="small"
                                sx={{
                                  ml: 1.5,
                                  backgroundColor: 'var(--success)',
                                  color: 'white',
                                  fontWeight: 700,
                                  minWidth: '28px'
                                }}
                              />
                            </Box>
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              {insights[index].insights.clinical_implications.map((implication, idx) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    p: 2,
                                    backgroundColor: 'var(--surface-2)',
                                    border: '1px solid var(--glass-border)',
                                    borderLeft: '4px solid var(--success)',
                                    borderRadius: 'var(--radius-sm)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      borderColor: 'var(--success)',
                                      backgroundColor: 'rgba(70, 130, 48, 0.05)',
                                      transform: 'translateX(4px)'
                                    }
                                  }}
                                >
                                  <Typography sx={{ 
                                    color: 'var(--text-primary)',
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    mb: 0.5,
                                    lineHeight: 1.6,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                  }}>
                                    {typeof implication === 'string' ? implication : implication.implication}
                                  </Typography>
                                  {typeof implication === 'object' && implication.implementation && (
                                    <Typography sx={{ 
                                      color: 'var(--text-muted)',
                                      fontSize: '0.85rem',
                                      lineHeight: 1.5,
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word'
                                    }}>
                                      Implementation: {implication.implementation}
                                    </Typography>
                                  )}
                                  {typeof implication === 'object' && implication.target_population && (
                                    <Chip
                                      label={implication.target_population}
                                      size="small"
                                      sx={{
                                        mt: 1,
                                        backgroundColor: 'rgba(70, 130, 48, 0.15)',
                                        color: 'var(--success)',
                                        fontSize: '0.75rem',
                                        fontWeight: 600
                                      }}
                                    />
                                  )}
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {/* Consensus Areas */}
                      {insights[index].insights.consensus_areas && insights[index].insights.consensus_areas.length > 0 && (
                        <Grid item xs={12} md={6}>
                          <Box className="modern-card" sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 2, borderBottom: '1px solid var(--glass-border)' }}>
                              <CheckCircleIcon sx={{ color: 'var(--success)', mr: 1.5, fontSize: '1.5rem' }} />
                              <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                                Consensus
                              </Typography>
                              <Chip
                                label={insights[index].insights.consensus_areas.length}
                                size="small"
                                sx={{
                                  ml: 1.5,
                                  backgroundColor: 'var(--success)',
                                  color: 'white',
                                  fontWeight: 700,
                                  minWidth: '28px'
                                }}
                              />
                            </Box>
                            <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 1, alignContent: 'flex-start' }}>
                              {insights[index].insights.consensus_areas.map((area, idx) => (
                                <Chip
                                  key={idx}
                                  label={typeof area === 'string' ? area : area.topic}
                                  sx={{
                                    backgroundColor: 'rgba(70, 130, 48, 0.15)',
                                    color: 'var(--success)',
                                    border: '1.5px solid var(--success)',
                                    fontWeight: 600,
                                    fontSize: '0.85rem',
                                    height: 'auto',
                                    py: 1,
                                    px: 1.5,
                                    '& .MuiChip-label': {
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-word'
                                    },
                                    '&:hover': {
                                      backgroundColor: 'rgba(70, 130, 48, 0.25)',
                                      transform: 'scale(1.02)'
                                    },
                                    transition: 'all 0.2s ease'
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {/* Controversial Areas */}
                      {insights[index].insights.controversial_areas && insights[index].insights.controversial_areas.length > 0 && (
                        <Grid item xs={12} md={6}>
                          <Box className="modern-card" sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 2, borderBottom: '1px solid var(--glass-border)' }}>
                              <QuestionMarkIcon sx={{ color: 'var(--warning)', mr: 1.5, fontSize: '1.5rem' }} />
                              <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                                Controversial
                              </Typography>
                              <Chip
                                label={insights[index].insights.controversial_areas.length}
                                size="small"
                                sx={{
                                  ml: 1.5,
                                  backgroundColor: 'var(--warning)',
                                  color: 'white',
                                  fontWeight: 700,
                                  minWidth: '28px'
                                }}
                              />
                            </Box>
                            <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 1, alignContent: 'flex-start' }}>
                              {insights[index].insights.controversial_areas.map((area, idx) => (
                                <Chip
                                  key={idx}
                                  label={typeof area === 'string' ? area : area.topic}
                                  sx={{
                                    backgroundColor: 'rgba(255, 193, 7, 0.15)',
                                    color: 'var(--warning)',
                                    border: '1.5px solid var(--warning)',
                                    fontWeight: 600,
                                    fontSize: '0.85rem',
                                    height: 'auto',
                                    py: 1,
                                    px: 1.5,
                                    '& .MuiChip-label': {
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-word'
                                    },
                                    '&:hover': {
                                      backgroundColor: 'rgba(255, 193, 7, 0.25)',
                                      transform: 'scale(1.02)'
                                    },
                                    transition: 'all 0.2s ease'
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {/* Patient Population Insights */}
                      {insights[index].insights.patient_population_insights && (
                        <Grid item xs={12}>
                          <Box className="modern-card" sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 2, borderBottom: '1px solid var(--glass-border)' }}>
                              <ScienceIcon sx={{ color: 'var(--neon-cyan)', mr: 1.5, fontSize: '1.5rem' }} />
                              <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                                Patient Population Insights
                              </Typography>
                            </Box>
                            <Grid container spacing={2}>
                              {insights[index].insights.patient_population_insights.age_ranges && (
                                <Grid item xs={12} md={6}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--neon-cyan)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Age Ranges</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.patient_population_insights.age_ranges}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              {insights[index].insights.patient_population_insights.gender_distribution && (
                                <Grid item xs={12} md={6}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--neon-cyan)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Gender Distribution</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.patient_population_insights.gender_distribution}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              {insights[index].insights.patient_population_insights.comorbidities && (
                                <Grid item xs={12} md={6}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--neon-cyan)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Comorbidities</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.patient_population_insights.comorbidities}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              {insights[index].insights.patient_population_insights.baseline_characteristics && (
                                <Grid item xs={12} md={6}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--neon-cyan)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Baseline Characteristics</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.patient_population_insights.baseline_characteristics}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              {insights[index].insights.patient_population_insights.exclusion_criteria && (
                                <Grid item xs={12}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--neon-cyan)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Exclusion Criteria</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.patient_population_insights.exclusion_criteria}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        </Grid>
                      )}

                      {/* Quantitative Insights */}
                      {insights[index].insights.quantitative_insights && (
                        <Grid item xs={12}>
                          <Box className="modern-card" sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 2, borderBottom: '1px solid var(--glass-border)' }}>
                              <TrendingUpIcon sx={{ color: 'var(--success)', mr: 1.5, fontSize: '1.5rem' }} />
                              <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                                Quantitative Insights
                              </Typography>
                            </Box>
                            <Grid container spacing={2}>
                              {insights[index].insights.quantitative_insights.sample_sizes && (
                                <Grid item xs={12} md={6}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--success)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Sample Sizes</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.quantitative_insights.sample_sizes}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              {insights[index].insights.quantitative_insights.effect_sizes && (
                                <Grid item xs={12} md={6}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--success)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Effect Sizes</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.quantitative_insights.effect_sizes}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              {insights[index].insights.quantitative_insights.success_rates && (
                                <Grid item xs={12} md={6}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--success)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Success Rates</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.quantitative_insights.success_rates}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              {insights[index].insights.quantitative_insights.timeframes && (
                                <Grid item xs={12} md={6}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--success)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Timeframes</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.quantitative_insights.timeframes}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              {insights[index].insights.quantitative_insights.demographics && (
                                <Grid item xs={12}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--success)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Demographics</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.quantitative_insights.demographics}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        </Grid>
                      )}

                      {/* Detailed Statistical Analysis */}
                      {insights[index].insights.detailed_statistical_analysis && (
                        <Grid item xs={12}>
                          <Box className="modern-card" sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 2, borderBottom: '1px solid var(--glass-border)' }}>
                              <TimelineIcon sx={{ color: 'var(--neon-cyan)', mr: 1.5, fontSize: '1.5rem' }} />
                              <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                                Detailed Statistical Analysis
                              </Typography>
                            </Box>
                            <Grid container spacing={2}>
                              {insights[index].insights.detailed_statistical_analysis.primary_outcomes && (
                                <Grid item xs={12} md={6}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--neon-cyan)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Primary Outcomes</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.detailed_statistical_analysis.primary_outcomes}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              {insights[index].insights.detailed_statistical_analysis.secondary_outcomes && (
                                <Grid item xs={12} md={6}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--neon-cyan)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Secondary Outcomes</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.detailed_statistical_analysis.secondary_outcomes}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              {insights[index].insights.detailed_statistical_analysis.confidence_intervals && (
                                <Grid item xs={12} md={6}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--neon-cyan)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Confidence Intervals</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.detailed_statistical_analysis.confidence_intervals}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              {insights[index].insights.detailed_statistical_analysis.effect_size_interpretation && (
                                <Grid item xs={12} md={6}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--neon-cyan)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Effect Size Interpretation</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.detailed_statistical_analysis.effect_size_interpretation}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              {insights[index].insights.detailed_statistical_analysis.statistical_power && (
                                <Grid item xs={12} md={6}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--neon-cyan)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Statistical Power</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.detailed_statistical_analysis.statistical_power}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              {insights[index].insights.detailed_statistical_analysis.meta_analysis_results && (
                                <Grid item xs={12} md={6}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--neon-cyan)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Meta-Analysis Results</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.detailed_statistical_analysis.meta_analysis_results}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        </Grid>
                      )}

                      {/* Temporal Trends */}
                      {insights[index].insights.temporal_trends && (
                        <Grid item xs={12}>
                          <Box className="modern-card" sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 2, borderBottom: '1px solid var(--glass-border)' }}>
                              <TimelineIcon sx={{ color: 'var(--neon-cyan)', mr: 1.5, fontSize: '1.5rem' }} />
                              <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                                Temporal Trends
                              </Typography>
                            </Box>
                            <Grid container spacing={2}>
                              {insights[index].insights.temporal_trends.publication_patterns && (
                                <Grid item xs={12}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--neon-cyan)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Publication Patterns</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.temporal_trends.publication_patterns}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              {insights[index].insights.temporal_trends.evolving_understanding && (
                                <Grid item xs={12}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--neon-cyan)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Evolving Understanding</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.temporal_trends.evolving_understanding}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              {insights[index].insights.temporal_trends.recent_developments && (
                                <Grid item xs={12}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--neon-cyan)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Recent Developments</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.temporal_trends.recent_developments}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        </Grid>
                      )}

                      {/* Safety & Adverse Events */}
                      {insights[index].insights.safety_and_adverse_events && insights[index].insights.safety_and_adverse_events.length > 0 && (
                        <Grid item xs={12}>
                          <Box className="modern-card" sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 2, borderBottom: '1px solid var(--glass-border)' }}>
                              <WarningIcon sx={{ color: 'var(--error)', mr: 1.5, fontSize: '1.5rem' }} />
                              <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                                Safety & Adverse Events
                              </Typography>
                              <Chip
                                label={insights[index].insights.safety_and_adverse_events.length}
                                size="small"
                                sx={{
                                  ml: 1.5,
                                  backgroundColor: 'var(--error)',
                                  color: 'white',
                                  fontWeight: 700,
                                  minWidth: '28px'
                                }}
                              />
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              {insights[index].insights.safety_and_adverse_events.map((event, idx) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    p: 2,
                                    backgroundColor: 'var(--surface-2)',
                                    border: '1px solid var(--glass-border)',
                                    borderLeft: '4px solid var(--error)',
                                    borderRadius: 'var(--radius-sm)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      borderColor: 'var(--error)',
                                      backgroundColor: 'rgba(244, 67, 54, 0.05)'
                                    }
                                  }}
                                >
                                  <Typography sx={{ 
                                    color: 'var(--text-primary)',
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    mb: 0.5,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                  }}>
                                    {typeof event === 'string' ? event : event.finding}
                                  </Typography>
                                  {typeof event === 'object' && event.severity && (
                                    <Typography sx={{ 
                                      color: 'var(--text-muted)',
                                      fontSize: '0.85rem',
                                      lineHeight: 1.5
                                    }}>
                                      Severity: {event.severity} | Frequency: {event.frequency || 'Not specified'}
                                    </Typography>
                                  )}
                                  {typeof event === 'object' && event.management && (
                                    <Typography sx={{ 
                                      color: 'var(--text-muted)',
                                      fontSize: '0.85rem',
                                      lineHeight: 1.5,
                                      mt: 0.5
                                    }}>
                                      Management: {event.management}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {/* Evidence Quality Assessment */}
                      {insights[index].insights.evidence_quality_assessment && (
                        <Grid item xs={12}>
                          <Box className="modern-card" sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 2, borderBottom: '1px solid var(--glass-border)' }}>
                              <CheckCircleIcon sx={{ color: 'var(--success)', mr: 1.5, fontSize: '1.5rem' }} />
                              <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                                Evidence Quality Assessment
                              </Typography>
                            </Box>
                            <Grid container spacing={2}>
                              {insights[index].insights.evidence_quality_assessment.high_quality_studies && (
                                <Grid item xs={12} md={6}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--success)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>High Quality Studies</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.evidence_quality_assessment.high_quality_studies}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              {insights[index].insights.evidence_quality_assessment.limitations && (
                                <Grid item xs={12} md={6}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--warning)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Limitations</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.evidence_quality_assessment.limitations}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              {insights[index].insights.evidence_quality_assessment.bias_concerns && (
                                <Grid item xs={12} md={6}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--error)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Bias Concerns</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.evidence_quality_assessment.bias_concerns}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              {insights[index].insights.evidence_quality_assessment.reliability && (
                                <Grid item xs={12} md={6}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--success)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Reliability</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.evidence_quality_assessment.reliability}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        </Grid>
                      )}

                      {/* Economic Considerations */}
                      {insights[index].insights.economic_considerations && (
                        <Grid item xs={12} md={6}>
                          <Box className="modern-card" sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 2, borderBottom: '1px solid var(--glass-border)' }}>
                              <TrendingUpIcon sx={{ color: 'var(--warning)', mr: 1.5, fontSize: '1.5rem' }} />
                              <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                                Economic Considerations
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              {insights[index].insights.economic_considerations.cost_effectiveness && (
                                <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                  <Typography sx={{ color: 'var(--warning)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Cost-Effectiveness</Typography>
                                  <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                    {insights[index].insights.economic_considerations.cost_effectiveness}
                                  </Typography>
                                </Box>
                              )}
                              {insights[index].insights.economic_considerations.resource_utilization && (
                                <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                  <Typography sx={{ color: 'var(--warning)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Resource Utilization</Typography>
                                  <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                    {insights[index].insights.economic_considerations.resource_utilization}
                                  </Typography>
                                </Box>
                              )}
                              {insights[index].insights.economic_considerations.budget_impact && (
                                <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                  <Typography sx={{ color: 'var(--warning)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Budget Impact</Typography>
                                  <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                    {insights[index].insights.economic_considerations.budget_impact}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {/* Regulatory & Guidelines */}
                      {insights[index].insights.regulatory_and_guideline_insights && (
                        <Grid item xs={12} md={6}>
                          <Box className="modern-card" sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 2, borderBottom: '1px solid var(--glass-border)' }}>
                              <ArticleIcon sx={{ color: 'var(--neon-cyan)', mr: 1.5, fontSize: '1.5rem' }} />
                              <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                                Regulatory & Guidelines
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              {insights[index].insights.regulatory_and_guideline_insights.regulatory_status && (
                                <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                  <Typography sx={{ color: 'var(--neon-cyan)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Regulatory Status</Typography>
                                  <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                    {insights[index].insights.regulatory_and_guideline_insights.regulatory_status}
                                  </Typography>
                                </Box>
                              )}
                              {insights[index].insights.regulatory_and_guideline_insights.guideline_recommendations && (
                                <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                  <Typography sx={{ color: 'var(--neon-cyan)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Guideline Recommendations</Typography>
                                  <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                    {insights[index].insights.regulatory_and_guideline_insights.guideline_recommendations}
                                  </Typography>
                                </Box>
                              )}
                              {insights[index].insights.regulatory_and_guideline_insights.labeling_considerations && (
                                <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                  <Typography sx={{ color: 'var(--neon-cyan)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Labeling Considerations</Typography>
                                  <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                    {insights[index].insights.regulatory_and_guideline_insights.labeling_considerations}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {/* Intervention Details */}
                      {insights[index].insights.intervention_details && (
                        <Grid item xs={12}>
                          <Box className="modern-card" sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 2, borderBottom: '1px solid var(--glass-border)' }}>
                              <ScienceIcon sx={{ color: 'var(--success)', mr: 1.5, fontSize: '1.5rem' }} />
                              <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                                Intervention Details
                              </Typography>
                            </Box>
                            <Grid container spacing={2}>
                              {insights[index].insights.intervention_details.dosage_ranges && (
                                <Grid item xs={12} md={6}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--success)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Dosage Ranges</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.intervention_details.dosage_ranges}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              {insights[index].insights.intervention_details.administration_methods && (
                                <Grid item xs={12} md={6}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--success)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Administration Methods</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.intervention_details.administration_methods}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              {insights[index].insights.intervention_details.treatment_duration && (
                                <Grid item xs={12} md={6}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--success)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Treatment Duration</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.intervention_details.treatment_duration}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              {insights[index].insights.intervention_details.follow_up_periods && (
                                <Grid item xs={12} md={6}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--success)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Follow-up Periods</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.intervention_details.follow_up_periods}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              {insights[index].insights.intervention_details.adherence_rates && (
                                <Grid item xs={12}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--success)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Adherence Rates</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.intervention_details.adherence_rates}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        </Grid>
                      )}

                      {/* Comparison with Alternatives */}
                      {insights[index].insights.comparison_with_alternatives && insights[index].insights.comparison_with_alternatives.length > 0 && (
                        <Grid item xs={12}>
                          <Box className="modern-card" sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 2, borderBottom: '1px solid var(--glass-border)' }}>
                              <TimelineIcon sx={{ color: 'var(--neon-cyan)', mr: 1.5, fontSize: '1.5rem' }} />
                              <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                                Comparison with Alternatives
                              </Typography>
                              <Chip
                                label={insights[index].insights.comparison_with_alternatives.length}
                                size="small"
                                sx={{
                                  ml: 1.5,
                                  backgroundColor: 'var(--neon-cyan)',
                                  color: 'white',
                                  fontWeight: 700,
                                  minWidth: '28px'
                                }}
                              />
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              {insights[index].insights.comparison_with_alternatives.map((comparison, idx) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    p: 2,
                                    backgroundColor: 'var(--surface-2)',
                                    border: '1px solid var(--glass-border)',
                                    borderLeft: '4px solid var(--neon-cyan)',
                                    borderRadius: 'var(--radius-sm)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      borderColor: 'var(--neon-cyan)',
                                      backgroundColor: 'rgba(108, 48, 130, 0.05)'
                                    }
                                  }}
                                >
                                  <Typography sx={{ 
                                    color: 'var(--text-primary)',
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    mb: 0.5,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                  }}>
                                    {typeof comparison === 'string' ? comparison : comparison.comparison}
                                  </Typography>
                                  {typeof comparison === 'object' && comparison.relative_effectiveness && (
                                    <Typography sx={{ 
                                      color: 'var(--text-muted)',
                                      fontSize: '0.85rem',
                                      lineHeight: 1.5,
                                      mt: 0.5
                                    }}>
                                      Relative Effectiveness: {comparison.relative_effectiveness}
                                    </Typography>
                                  )}
                                  {typeof comparison === 'object' && comparison.contexts && (
                                    <Typography sx={{ 
                                      color: 'var(--text-muted)',
                                      fontSize: '0.85rem',
                                      lineHeight: 1.5,
                                      mt: 0.5
                                    }}>
                                      Contexts: {comparison.contexts}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {/* Real-World Evidence */}
                      {insights[index].insights.real_world_evidence && (
                        <Grid item xs={12}>
                          <Box className="modern-card" sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 2, borderBottom: '1px solid var(--glass-border)' }}>
                              <LightbulbIcon sx={{ color: 'var(--success)', mr: 1.5, fontSize: '1.5rem' }} />
                              <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                                Real-World Evidence
                              </Typography>
                            </Box>
                            <Grid container spacing={2}>
                              {insights[index].insights.real_world_evidence.observational_studies && (
                                <Grid item xs={12}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--success)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Observational Studies</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.real_world_evidence.observational_studies}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              {insights[index].insights.real_world_evidence.registry_data && (
                                <Grid item xs={12}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--success)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Registry Data</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.real_world_evidence.registry_data}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              {insights[index].insights.real_world_evidence.post_marketing_surveillance && (
                                <Grid item xs={12}>
                                  <Box sx={{ p: 2, backgroundColor: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)' }}>
                                    <Typography sx={{ color: 'var(--success)', fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Post-Marketing Surveillance</Typography>
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                      {insights[index].insights.real_world_evidence.post_marketing_surveillance}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        </Grid>
                      )}
                    </Grid>

                    {/* Metadata footer */}
                    <Divider sx={{ my: 3, borderColor: 'var(--glass-border)' }} />
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'flex-start', 
                      alignItems: 'center',
                      p: 2,
                      backgroundColor: 'var(--surface-2)',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--glass-border)'
                    }}>
                      <Typography variant="caption" sx={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                        🕒 Generated: {new Date(insights[index].generated_at).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box className="modern-card" sx={{ textAlign: 'center', py: 6, backgroundColor: 'var(--surface-2)' }}>
                    <LightbulbIcon sx={{ fontSize: 64, color: 'var(--neon-cyan)', mb: 2, opacity: 0.6 }} />
                    <Typography sx={{ color: 'var(--text-primary)', mb: 2, fontWeight: 500 }}>
                      No insights generated yet
                    </Typography>
                    <Typography sx={{ color: 'var(--text-muted)', mb: 3, fontSize: '0.9rem' }}>
                      Click below to generate AI-powered insights for this query
                    </Typography>
                    <Button
                      onClick={() => fetchInsights(index)}
                      className="btn-modern-primary"
                      startIcon={<AutoAwesomeIcon />}
                      sx={{
                        background: 'var(--accent-gradient)',
                        color: 'white',
                        fontWeight: 600,
                        px: 3,
                        py: 1,
                        '&:hover': {
                          boxShadow: '0 6px 20px rgba(108, 48, 130, 0.4)'
                        }
                      }}
                    >
                      Generate Insights
                    </Button>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      ) : (
        <Box 
          className="modern-card"
          sx={{
            p: 6,
            textAlign: 'center',
            backgroundColor: 'var(--surface-2)',
            border: '2px dashed var(--glass-border)',
            borderRadius: 'var(--radius-lg)'
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 80, color: 'var(--neon-cyan)', mb: 2, opacity: 0.5 }} />
          <Typography variant="h5" sx={{ color: 'var(--text-primary)', mb: 1, fontWeight: 600 }}>
            No Research Queries Yet
          </Typography>
          <Typography variant="body1" sx={{ color: 'var(--text-muted)', maxWidth: 500, mx: 'auto' }}>
            Create a research query from the Search tab and classify articles to generate AI-powered insights
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default ResearchInsights;

